import { brand } from '@websrc/App/Helpers/brand'
import { useLocale } from '@websrc/App/Hooks/useLocale'
import { usePath } from '@websrc/App/Hooks/usePath'
import { buildOrganisationSchema } from '@websrc/Build/buildOrganisationSchema'
import { OtherLocaleUrl } from '@websrc/Build/Pages/getOtherLocaleUrls'
import { mapSEOBlog } from '@websrc/DataMappers/mapSEO'
import { ensureTrailingSlash } from '@websrc/Util/ensureTrailingSlash'
import React from 'react'
import { Helmet } from 'react-helmet'
import { Product, WithContext } from 'schema-dts'

// Props passed specifically for SEO when building pages
export type SEOBlogProps = ReturnType<typeof mapSEOBlog> & {
  otherLocaleUrls?: OtherLocaleUrl[]
  productSchema?: WithContext<Product>
}

export const SEOBlog = ({
  description,
  title,
  keywords,
  image,
  authorName,
  datePublished,
  dateModified,
  url,
  otherLocaleUrls,
  productSchema,
  disableIndex,
  canonical_url,
}: SEOBlogProps) => {
  const currentLocale = useLocale()
  const path = usePath()
  const [lang, country] = currentLocale.split('-')
  const finalLocale = `${lang}_${country.toUpperCase()}`
  const baseURL = process.env.SITE_URL
  const canonicalHref = canonical_url ? canonical_url : ensureTrailingSlash(`${baseURL}${path}`)
  const organisationSchema = buildOrganisationSchema(baseURL, currentLocale)

  // Blog Article JSON-LD structured data
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title ?? '',
    description: description ?? '',
    image: image?.url ?? '',
    author: {
      '@type': 'Person',
      name: authorName ?? '',
    },
    datePublished: datePublished ?? '',
    dateModified: dateModified ?? datePublished ?? '',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url ?? '',
    },
    publisher: {
      '@type': 'Organization',
      name: 'LEGO Education',
      url: 'https://education.lego.com',
      sameAs: [
        'https://www.linkedin.com/company/lego-education/',
        'https://www.facebook.com/LEGOeducationOfficial',
        'https://x.com/LEGO_Education',
        'https://www.youtube.com/LEGOeducation',
      ],
      logo: {
        '@type': 'ImageObject',
        url: 'https://upload.wikimedia.org/wikipedia/commons/2/24/LEGO_logo.svg',
      },
    },
  }

  return (
    <Helmet>
      <html lang={currentLocale} />

      <title>{title}</title>

      <link href={canonicalHref} rel={'canonical'} />

      <meta content={description ?? ''} name={'description'} />
      <meta content={keywords ?? ''} name={'keywords'} />
      {disableIndex && <meta content={'noindex,nofollow'} name={'robots'} />}
      <meta
        content={'width=device-width,initial-scale=1,shrink-to-fit=no,viewport-fit=cover'}
        name={'viewport'}
      />

      {/* OpenGraph tags */}
      <meta content={'article'} property={'og:type'} />
      <meta content={brand.name} property={'og:site_name'} />
      <meta content={finalLocale} property={'og:locale'} />
      <meta content={title ?? ''} property={'og:title'} />
      <meta content={description ?? ''} property={'og:description'} />
      <meta content={url ?? ''} property={'og:url'} />
      {image?.url && <meta content={image.url} property={'og:image'} />}

      {/* Twitter tags */}
      <meta content={'summary_large_image'} name={'twitter:card'} />
      <meta content={'education.lego.com'} name={'twitter:domain'} />
      <meta content={url ?? ''} name={'twitter:url'} />
      <meta content={title ?? ''} name={'twitter:title'} />
      <meta content={description ?? ''} name={'twitter:description'} />
      {image?.url && <meta content={image.url} name={'twitter:image'} />}

      {/* Alternate locales */}
      {otherLocaleUrls?.map((localeUrl) => (
        <link
          href={String(baseURL) + localeUrl.url}
          hrefLang={localeUrl.locale}
          key={localeUrl.locale}
          rel={'alternate'}
        />
      ))}

      {/* Product schema if present */}
      {productSchema && (
        <script id={'product-schema'} type={'application/ld+json'}>
          {JSON.stringify(productSchema)}
        </script>
      )}

      {/* Organization schema for en-us */}
      {currentLocale === 'en-us' && (
        <script id={'organization-schema'} type={'application/ld+json'}>
          {JSON.stringify(organisationSchema)}
        </script>
      )}

      {/* Blog Article JSON-LD */}
      <script type={'application/ld+json'}>{JSON.stringify(articleJsonLd)}</script>
    </Helmet>
  )
}
